import React, { useEffect, useState, useCallback } from 'react';
import {
  Card,
  Table,
  Space,
  Button,
  Typography,
  App,
  Tooltip,
  Tag,
  Modal,
  Select,
  Col,
  Row,
} from 'antd';
import {
  BellOutlined,
  DeleteOutlined,
  LeftOutlined,
  ReloadOutlined,
  SwapOutlined,
} from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import { Popconfirm } from 'antd';
import { alertJudge, changeJudge, deleteJudge, listJudgesCompetitions } from '../../services/evaluations';
import { getCompetitionById } from '../../services/competitions';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { listAvailableJudges } from '../../services/catalogs';
import usePermissions from '../../hooks/usePermissions';

dayjs.extend(utc);
dayjs.extend(timezone);
const { Title, Text } = Typography;
const { useApp } = App;

const STATUS_COLORS = {
  'Sin Evaluar': 'grey',
  'En Curso': '#BA955E',
  'Terminada': 'green',
  'Critical': 'red',
};


const CompetitionJudgesList = () => {
  const { message } = useApp();
  const navigate = useNavigate();
  const { id } = useParams();

  const [data, setData] = useState([]);
  const [competitionData, setCompetitionData] = useState(null);
  const [loading, setLoading] = useState(false);

  const { can } = usePermissions();

  const [isChangeModalOpen, setIsChangeModalOpen] = useState(false);
  const [availableJudges, setAvailableJudges] = useState([]);
  const [selectedJudge, setSelectedJudge] = useState(null);
  const [judgeToReplace, setJudgeToReplace] = useState(null);

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const [sorter, setSorter] = useState({});

  // filtros aplicados
  const [status, setStatus] = useState(null);

  // filtros draft
  // const [draftStatus, setDraftStatus] = useState(null);

  useEffect(() => {
    setLoading(true);
    getCompetitionById(id)
      .then((competition) => {
        setCompetitionData(competition);
        
      })
      .catch((e) => {
        console.error('Error al cargar la competencia:', e);
        message.error('No se pudo cargar la competencia');
      })
      .finally(() => {
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, message]);

  const fetchData = useCallback(
    async (
      page,
      pageSize,
      srt,
      st
    ) => {
      setLoading(true);
      try {
        const params = {
          page: page || pagination.current,
          pageSize: pageSize || pagination.pageSize,
          sortField: srt?.field,
          sortOrder: srt?.order,
          ...(st && { status: st }),
        };

        const { items, total } = await listJudgesCompetitions(id,params);

        setData(items || []);
        setPagination(prev => ({
          ...prev,
          current: page || prev.current,
          pageSize: pageSize || prev.pageSize,
          total: total || 0,
        }));
      } catch {
        message.error('No se pudieron cargar las competencias');
      } finally {
        setLoading(false);
      }
    },
    [pagination, message, id]
  );

  useEffect(() => {
    fetchData(1, pagination.pageSize, null, null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyFilters = ({ status = null } = {}) => {
    setStatus(status);

    fetchData(
      1,
      pagination.pageSize,
      sorter,
      status
    );
  };

  const handleSearch = () => {
    applyFilters();
  };

  const handleDelete = async (record) => {
    if (!competitionData?.id) return;

    const isCompetitionActive = competitionData?.status?.description === 'En Curso';
    const isTerminated = record?.evaluationStatus === 'Terminada';
    const notStarted = record?.submitted === 0;

    if (!isCompetitionActive || isTerminated || !notStarted) {
      message.warning('No se puede eliminar este juez');
      return;
    }

    try {
      setLoading(true);

      await deleteJudge(record.competitionId, record.userId);

      message.success('Juez eliminado correctamente');

      fetchData(
        1,
        pagination.pageSize,
        sorter,
        status
      );

    } catch (error) {
      console.error(error);
      message.error(error?.response?.data?.error?.message || 'No se pudo eliminar el juez');
    } finally {
      setLoading(false);
    }
  };

  const handleAlert = async (record) => {
    if (!competitionData?.id) return;

    const isCompetitionActive =
      competitionData?.status?.description === 'En Curso';

    const isTerminated =
      record?.evaluationStatus === 'Terminada';

    if (!isCompetitionActive || isTerminated) {
      message.warning('No se puede enviar alerta a este juez');
      return;
    }

    try {
      setLoading(true);

      await alertJudge(record.competitionId, record.userId);

      message.success('Alerta enviada correctamente al juez');

    } catch (error) {
      console.error(error);
      message.error(
        error?.response?.data?.message ||
        'No se pudo enviar la alerta al juez'
      );
    } finally {
      setLoading(false);
    }
  };


  const handleChange = async (record) => {
    if (!competitionData?.id) return;

    const isCompetitionActive =
      competitionData?.status?.description === 'En Curso';

    const isTerminated =
      record?.evaluationStatus === 'Terminada';

    const notStarted =
      record?.submitted === 0;

    if (!isCompetitionActive || isTerminated || !notStarted) {
      message.warning('No se puede cambiar este juez');
      return;
    }

    try {
      setLoading(true);

      const judges = await listAvailableJudges(record.competitionId);

      setAvailableJudges(judges);
      setJudgeToReplace(record);
      setIsChangeModalOpen(true);

    } catch (error) {
      console.error(error);
      message.error(
        error?.response?.data?.message ||
        'No se pudieron obtener los jueces disponibles'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmChange = async () => {
    if (!selectedJudge || !judgeToReplace) return;

    try {
      setLoading(true);

      await changeJudge(
        competitionData.id,
        {selectedJudge, judgeToReplace: judgeToReplace.userId}
      );

      message.success('Juez cambiado correctamente');

      setIsChangeModalOpen(false);
      setSelectedJudge(null);
      setJudgeToReplace(null);

      fetchData(
        1,
        pagination.pageSize,
        sorter,
        status
      );

    } catch (error) {
      console.error(error);
      message.error(
        error?.response?.data?.message ||
        'No se pudo cambiar el juez'
      );
    } finally {
      setLoading(false);
    }
  };


  const onTableChange = (pag, filters, srt) => {
    setSorter(srt || {});
    fetchData(
      pag.current,
      pag.pageSize,
      srt,
      status
    );
  };
  const renderActions = (record) => {
    const isCompetitionActive = competitionData?.status?.description === 'En Curso';
    const isTerminated = record.evaluationStatus === 'Terminada';
    const notStarted = record.submitted === 0;

    const canModify = isCompetitionActive && !isTerminated;
    const canEdit = canModify && notStarted;

    const now = dayjs().tz('America/Monterrey');
    if(now.isAfter(evaluationRange?.end))
      return null;

    return (
      <Space>

        {!isTerminated && can('competitions_eval.notifiy') && (
          <Popconfirm
            title="¿Enviar alerta?"
            description="Se enviará un correo de alerta al juez."
            onConfirm={() => handleAlert(record)}
            okText="Sí"
            cancelText="No"
            disabled={!canModify}
          >
            <Tooltip title="Enviar alerta">
              <Button
                type="primary"
                warning
                icon={<BellOutlined />}
                disabled={!canModify}
              />
            </Tooltip>
          </Popconfirm>
        )}
        
        {notStarted && can('competitions_eval.assign_judges') && (
          <Tooltip title="Cambiar juez">
            <Button
              type="default"
              icon={<SwapOutlined />}
              disabled={!canEdit}
              onClick={() => handleChange(record)}
            />
          </Tooltip>
        )}

        {notStarted && can('competitions_eval.delete') && (
          <Popconfirm
            title="¿Eliminar juez?"
            description="Se eliminará el juez y todas sus evaluaciones pendientes."
            onConfirm={() => handleDelete(record)}
            okText="Sí"
            cancelText="No"
            disabled={!canEdit}
          >
            <Tooltip title="Eliminar juez">
              <Button
                type="primary"
                danger
                icon={<DeleteOutlined />}
                disabled={!canEdit}
              />
            </Tooltip>
          </Popconfirm>
        )}

      </Space>
    );
  };


  const evaluationRange = React.useMemo(() => {
    if (!competitionData?.stages?.length) {
      return {
        startText: '-',
        endText: null,
        isClosingSoon: false,
      };
    }

    const stage = competitionData.stages.find(
      (s) => s?.type === 'EVALUATION'
    );

    if (!stage) {
      return {
        startText: '-',
        endText: null,
        start: null,
        end: null,
        isClosingSoon: false,
      };
    }

    const buildDateTime = (date, time) => {
      if (!date || !time) return null;

      const datePart = dayjs.utc(date).format('YYYY-MM-DD'); // forzar UTC
      const dateTime = dayjs.utc(`${datePart} ${time}`, 'YYYY-MM-DD HH:mm');

      return dateTime.isValid() ? dateTime : null;
    };

    const start = buildDateTime(stage.startDate, stage.startTime);
    const end = buildDateTime(stage.endDate, stage.endTime);

    const startText = start
      ? start.format('DD/MM/YYYY hh:mm A')
      : '-';

    const endText = end
      ? end.format('DD/MM/YYYY hh:mm A')
      : null;

    let isClosingSoon = false;

    if (end) {
      const hoursDiff = end.diff(dayjs(), 'hour', true);
      if (hoursDiff <= 12 && hoursDiff > 0) {
        isClosingSoon = true;
      }
    }

    return { startText, endText, isClosingSoon, start, end };
  }, [competitionData]);



  const columns = [
    {
      title: 'Nombre Completo',
      dataIndex: 'userId',
      sorter: true,
      render: (_, r) => (`${r?.user?.firstName} ${r?.user?.lastName} ${r?.user?.secondLastName || ''}`) || '-',
    },
    {
      title: 'Evaluacion',
      dataIndex: 'evaluationStatus',
      render: (status, r) => {
        const isCritical = r.critical;

        const displayStatus = isCritical ? 'Critical' : status;

        return (
          <Tag color={STATUS_COLORS[displayStatus] || 'default'}>
            {status}
          </Tag>
        );
      },
    },
    {
      title: 'Acciones',
      align: 'center',
      render: (_, r) => renderActions(r),
    },
  ];

  const handleBack = () => {
    navigate('/evaluations/progress/all');
  };

  return (
    <>
      <div style={{ padding: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Button 
                type="text" 
                icon={<LeftOutlined style={{ fontSize: '24px', strokeWidth: 4 }} />} 
                style={{ 
                padding: 0,
                color: '#BA955E',
                fontSize: '24px',
                fontWeight: '700',
                height: 'auto'
                }}
                onClick={handleBack}
            />
            <Title level={3} style={{ marginBottom: 0 }}>
            Jueces en Evaluación
            </Title>
        </div>

        <Card>
          <Space wrap style={{ marginBottom: 16 }}>
            
            <Row justify="space-between" align="middle" gutter={[32, 16]} style={{ marginBottom: 16 }}>
              {/* FOLIO y modalidad */}
              <Col flex="auto">
                <Text strong style={{ fontSize: 16, marginRight: 8 }}>
                  {competitionData?.folio || ''}
                </Text>
                {competitionData?.modality && (
                  <Text>
                    {competitionData.modality.name || competitionData.modality.description}
                  </Text>
                )}
              </Col>

              {/* Regiones */}
              {competitionData?.regions && (
                <Col flex="auto" style={{ textAlign: 'right' }}>
                  <Text>
                    {competitionData.regions.length > 0
                      ? competitionData.regions
                          .map(r => r.region?.description)
                          .filter(Boolean)
                          .join(', ')
                      : competitionData?.region?.description || '-'}
                  </Text>
                </Col>
              )}

              {/* Rango de evaluación */}
              {competitionData?.stages && (
                <Col flex="none">
                  <Text>
                    {evaluationRange.startText}
                    {evaluationRange.endText && (
                      <>
                        {' – '}
                        <Text type={evaluationRange.isClosingSoon ? 'danger' : undefined}>
                          {evaluationRange.endText}
                        </Text>
                      </>
                    )}
                  </Text>
                </Col>
              )}
              <Col flex="auto" style={{ textAlign: 'right' }}>
                <Tooltip title="Buscar">
                  <Button
                    type="primary"
                    icon={<ReloadOutlined />}
                    onClick={handleSearch}
                  />
                </Tooltip>
              </Col>
            </Row>
          </Space>

          <Table
            rowKey="id"
            columns={columns}
            dataSource={data}
            loading={loading}
            scroll={{ x: 'max-content' }}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
              pageSizeOptions: [10, 20, 50, 100],
              showTotal: t => `Total: ${t} competencias`,
            }}
            onChange={onTableChange}
          />
        </Card>
      </div>
      <Modal
        title="Cambiar juez"
        open={isChangeModalOpen}
        onCancel={() => {
          setIsChangeModalOpen(false);
          setSelectedJudge(null);
        }}
        onOk={handleConfirmChange}
        okButtonProps={{ disabled: !selectedJudge }}
      >
        <Select
          style={{ width: '100%' }}
          placeholder="Selecciona un juez"
          value={selectedJudge ?? undefined}
          onChange={(value) => setSelectedJudge(value)}
          options={availableJudges.map(judge => ({
            label: `${judge.firstName} ${judge.lastName} ${judge.secondLastName || ''}`,
            value: judge.id
          }))}
        />
      </Modal>
    </>
  );
};

export default CompetitionJudgesList;
